import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { videosApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Lock, Play, Sparkles, X } from 'lucide-react';
import { logger } from '../lib/logger';

function VideoCard({ video, onPlay }) {
  const { title, description, category, duration_seconds, locked, youtube_id } = video;
  const thumb = `https://img.youtube.com/vi/${youtube_id}/hqdefault.jpg`;
  const mins = Math.max(1, Math.round((duration_seconds || 0) / 60));

  return (
    <Card
      className="group overflow-hidden border-border hover:shadow-lg transition-shadow duration-200"
      data-testid={`video-card-${video.video_id}`}
    >
      <button
        type="button"
        onClick={() => onPlay(video)}
        className="relative block w-full aspect-video bg-[#0a0d0e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C54]"
        aria-label={`Play ${title}${locked ? ' preview' : ''}`}
        data-testid={`video-play-${video.video_id}`}
      >
        <img
          src={thumb}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-200"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-sm transition-transform duration-200 group-hover:scale-110 ${locked ? 'bg-white/10 border border-white/30' : 'bg-[#004C54]/90 border border-white/30'}`}>
            {locked ? <Lock className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
          </div>
        </div>
        <div className="absolute top-2 left-2 flex gap-1.5">
          <Badge className="bg-black/60 border-white/20 text-white text-[10px] backdrop-blur-sm">{category}</Badge>
          {locked && (
            <Badge className="bg-[#4FBCC7]/95 border-0 text-[#0a0d0e] text-[10px] font-bold">Preview</Badge>
          )}
        </div>
        <div className="absolute bottom-2 right-2">
          <Badge className="bg-black/60 border-white/20 text-white text-[10px] backdrop-blur-sm font-mono">{mins}m</Badge>
        </div>
      </button>
      <CardContent className="p-4">
        <h3 className="font-heading text-[15px] font-bold leading-snug mb-1.5 line-clamp-2">{title}</h3>
        <p className="text-[13px] text-foreground/65 line-clamp-2">{description}</p>
      </CardContent>
    </Card>
  );
}

function VideoPlayerModal({ video, onClose, navigate }) {
  if (!video) return null;
  const isPreview = video.locked;
  const src = `https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0${isPreview ? `&start=0&end=${video.preview_seconds || 30}` : ''}`;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="video-player-modal"
    >
      <div
        className="relative w-full max-w-4xl bg-[#0a0d0e] rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          aria-label="Close video"
          data-testid="video-player-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="aspect-video bg-black">
          <iframe
            title={video.title}
            src={src}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="video-iframe"
          />
        </div>
        <div className="p-5 sm:p-6 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-[#004C54]/30 text-[#4FBCC7] border-[#004C54]/40">{video.category}</Badge>
            {isPreview && <Badge className="bg-[#4FBCC7] text-[#0a0d0e] border-0 font-bold">Preview only</Badge>}
          </div>
          <h3 className="font-heading text-xl font-bold mb-2">{video.title}</h3>
          <p className="text-sm text-white/70 mb-4">{video.description}</p>
          {isPreview && (
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between border-t border-white/10 pt-4">
              <div className="text-sm text-white/80">
                <Sparkles className="inline h-4 w-4 mr-1.5 text-[#4FBCC7]" />
                Unlock the full library on Apex AI or 1:1 with Coach Lewis.
              </div>
              <Button
                onClick={() => { onClose(); navigate('/app/pricing'); }}
                className="bg-[#4FBCC7] hover:bg-[#3aa6b1] text-[#0a0d0e] font-bold"
                data-testid="video-upgrade-cta"
              >
                See plans
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Videos() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    // Mount-only load. `videosApi.list`, `logger`, and the state setters
    // (setLoading/setData/setError) are all module-stable / React-stable
    // references — adding them to the dependency array would be churn
    // with no semantic effect. Intentional empty array.
    setLoading(true);
    videosApi.list()
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { logger.error('videos.list failed', e); setError(e.message || 'Could not load videos.'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!data?.videos) return [];
    if (activeCategory === 'All') return data.videos;
    return data.videos.filter((v) => v.category === activeCategory);
  }, [data, activeCategory]);

  const tier = data?.tier || 'free';
  const lockedCount = (data?.videos || []).filter((v) => v.locked).length;

  return (
    <div className="space-y-6" data-testid="videos-page">
      <div>
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">
          Mental Performance Library
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Train your mind, on demand</h1>
        <p className="text-[15px] text-foreground/70 mt-1 max-w-2xl">
          Short, focused videos on confidence, focus, pre-game routines, and recovery — curated for athletes and the people who support them.
        </p>
      </div>

      {tier === 'free' && lockedCount > 0 && (
        <Card className="border-2 border-[#4FBCC7]/40 bg-gradient-to-r from-[#4FBCC7]/10 to-[#004C54]/10" data-testid="videos-upgrade-banner">
          <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#004C54]/15 flex items-center justify-center shrink-0">
                <Lock className="h-4 w-4 text-[#004C54]" />
              </div>
              <div>
                <div className="font-semibold text-[14px]">You're on the Free plan</div>
                <div className="text-[13px] text-foreground/65">
                  {lockedCount} of {data?.videos?.length} videos show 30-second previews. Upgrade to unlock the full library.
                </div>
              </div>
            </div>
            <Button onClick={() => navigate('/app/pricing')} className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold" data-testid="videos-see-plans">
              See plans
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1" role="tablist" aria-label="Video categories">
        {['All', ...(data?.categories || [])].map((cat) => {
          const active = cat === activeCategory;
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-colors ${active ? 'bg-[#004C54] text-white' : 'bg-[#F0F4F4] text-foreground/70 hover:bg-[#E2EAEA]'}`}
              data-testid={`video-cat-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden border-border">
              <Skeleton className="aspect-video w-full" />
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && error && (
        <Card className="border-destructive/30 bg-destructive/5" data-testid="videos-error">
          <CardContent className="p-5">
            <div className="font-semibold text-destructive mb-1">Couldn't load videos</div>
            <div className="text-sm text-foreground/70 mb-3">{error}</div>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">Retry</Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Card className="border-border" data-testid="videos-empty">
          <CardContent className="p-6 text-center text-foreground/70">
            Nothing in this category yet — check back soon.
          </CardContent>
        </Card>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
          {filtered.map((v) => (
            <VideoCard key={v.video_id} video={v} onPlay={setPlaying} />
          ))}
        </div>
      )}

      <VideoPlayerModal video={playing} onClose={() => setPlaying(null)} navigate={navigate} />
    </div>
  );
}
